/**
 * Cleovivra: inline quick-add on product cards.
 *
 * Horizon's own quick-add opens a modal sheet (image, title, price, quantity,
 * Add to cart, Buy it now). The brand reference instead reveals a compact size
 * row over the bottom of the card image — tap a size, straight into the cart —
 * with the "+" flipping to "×". This replaces the native control with that.
 *
 * The native quick-add markup is hidden by CSS rather than removed, so the
 * theme's own component keeps working if this script fails to load.
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const CARD = 'product-card';
const GALLERY = '.card-gallery';

/** Cache product JSON per handle so re-opening a card doesn't refetch. */
const productCache = new Map();

async function loadProduct(handle) {
  if (productCache.has(handle)) return productCache.get(handle);
  const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  productCache.set(handle, request);
  return request;
}

function closeAll(except) {
  for (const open of document.querySelectorAll('.cleo-qa[data-open="true"]')) {
    if (open === except) continue;
    open.dataset.open = 'false';
    open.querySelector('.cleo-qa__toggle')?.setAttribute('aria-expanded', 'false');
  }
}

/**
 * Open the cart drawer after adding.
 *
 * The theme has an auto-open path (cart-drawer-component[auto-open] reacting to
 * a cartLinesUpdate with action 'add'), and the setting for it is enabled — but
 * it does not fire here even for the theme's own add-to-cart, so this opens the
 * drawer explicitly. `<theme-drawer>` owns the dialog lifecycle and exposes
 * open(); clicking the cart link is the fallback if that's ever unavailable.
 */
function openCartDrawer() {
  /*
   * Target #cart-drawer by id, never `document.querySelector('theme-drawer')`.
   * There are several theme-drawer elements on the page and the first one is
   * the header's menu drawer — opening that shows an empty white panel that
   * the cart's close button can't dismiss.
   */
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || drawer.isOpen) return;

  if (typeof drawer.open === 'function') {
    drawer.open();
    return;
  }
  /** @type {HTMLElement | null} */
  const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
  trigger?.click();
}

async function addToCart(variantId, button, root) {
  button.disabled = true;
  root.dataset.busy = 'true';

  /*
   * The theme's cart listeners read `event.promise` and await it — they ignore
   * a payload passed directly on the event. So follow product-form.js: dispatch
   * up front with a deferred promise, then resolve it once the cart is known.
   */
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!response.ok) throw new Error(`add failed: ${response.status}`);

    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'cleo-quick-add',
        sourceId: 'cleo-quick-add',
        itemCount: cart.item_count,
      },
    });

    root.dataset.added = 'true';
    root.dataset.open = 'false';
    root.querySelector('.cleo-qa__toggle')?.setAttribute('aria-expanded', 'false');
    setTimeout(() => (root.dataset.added = 'false'), 900);

    openCartDrawer();
  } catch (error) {
    console.error('[cleo-quick-add]', error);
    // Reject so listeners awaiting the promise don't hang forever.
    deferred.reject?.(error);
    root.dataset.error = 'true';
    setTimeout(() => (root.dataset.error = 'false'), 1600);
  } finally {
    button.disabled = false;
    root.dataset.busy = 'false';
  }
}

function renderOptions(root, product) {
  const list = root.querySelector('.cleo-qa__options');
  if (!list || list.childElementCount) return;

  for (const variant of product.variants) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cleo-qa__option';
    button.textContent = variant.options?.[0] ?? variant.title;
    button.disabled = !variant.available;
    if (!variant.available) button.setAttribute('aria-disabled', 'true');
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (variant.available) addToCart(variant.id, button, root);
    });
    list.appendChild(button);
  }
}

function enhance(card) {
  // Only where Horizon decided a quick-add belongs (in stock, purchasable).
  if (!card.querySelector('.quick-add')) return;
  const gallery = card.querySelector(GALLERY);
  const link = card.querySelector('a[href*="/products/"]');
  if (!gallery || !link) return;

  const handle = link.getAttribute('href').split('/products/')[1]?.split(/[?#]/)[0];
  if (!handle) return;

  const root = document.createElement('div');
  root.className = 'cleo-qa';
  root.dataset.open = 'false';
  root.innerHTML = `
    <button type="button" class="cleo-qa__toggle" aria-expanded="false" aria-label="Quick add">
      <span class="cleo-qa__icon" aria-hidden="true"></span>
    </button>
    <div class="cleo-qa__panel"><div class="cleo-qa__options"></div></div>
  `;

  const toggle = root.querySelector('.cleo-qa__toggle');
  toggle.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const willOpen = root.dataset.open !== 'true';
    closeAll(root);
    if (willOpen) {
      const product = await loadProduct(handle);
      if (product) renderOptions(root, product);
    }
    root.dataset.open = String(willOpen);
    toggle.setAttribute('aria-expanded', String(willOpen));
  });

  gallery.appendChild(root);
}

function init(scope = document) {
  for (const card of scope.querySelectorAll(CARD)) {
    if (card.dataset.cleoQa) continue;
    card.dataset.cleoQa = '1';
    enhance(card);
  }
}

document.addEventListener('click', () => closeAll(null));
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeAll(null);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

/* Sections re-render in the theme editor, and the collection toggle swaps cards. */
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener(StandardEvents.cartLinesUpdate, () => init());
